import React, { useState } from 'react';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import { ZEROMINS, HOUR, MAX } from './TimeCostConstants';

import './customSlider.styles.scss';
import { FONT_COLOR } from '../../../utils';

const timeSliderColours = {
	sliderBlue: '#6A6E9B',
	sliderGrey: '#00073380',
	red: '#FF8787',
	tooltipBg: 'rgba(3,4,34,1)',
};

const TimeSlider = styled(Slider)(({ theme }) => ({
	color: timeSliderColours.sliderBlue,
	'& .MuiSlider-thumb': {
		backgroundColor: timeSliderColours.sliderBlue,
	},
	'.MuiSlider-colorSecondary': {
		color: timeSliderColours.red,
	},

	'& .MuiSlider-rail': {
		color: timeSliderColours.sliderGrey,
	},
	'& .MuiSlider-valueLabelOpen': {
		backgroundColor: timeSliderColours.tooltipBg,
	},
	'& .MuiSlider-markLabel': {
		color: FONT_COLOR,
		position: 'absolute',
		top: '-20px',
	},
}));

interface TimeCostSliderProps {
	name: string;
	onChange?: (n: string, value: number) => void;
	defaultValue?: number | null;
}

export const TimeCostSlider = ({
	name,
	onChange,
	defaultValue,
}: TimeCostSliderProps) => {
	const d = defaultValue ? (defaultValue / 100) * MAX : undefined;
	const [sliderValue, setSliderValue] = useState<number>(d || ZEROMINS);

	const handleChange = (e: Event, newValue: number | number[]) => {
		const result = newValue as number;
		const normalisedResult = Math.floor((result / MAX) * 100);
		setSliderValue(result);

		if (onChange) {
			onChange(name, normalisedResult);
		}
	};

	const formatValue = (value: number) => {
		if (value <= ZEROMINS) {
			const distanceFromZero = ZEROMINS - value;
			const hours = Math.floor(distanceFromZero / HOUR);

			if (value === ZEROMINS) {
				return (
					<div className="time-tooltip">
						<p className="time-tooltip-neutral">Neutral</p>
						<p className="time-tooltip-bigNum">{distanceFromZero}</p>
						<p>{distanceFromZero === 1 ? 'minute' : 'minutes'}</p>
					</div>
				);
			}

			if (distanceFromZero < HOUR) {
				return (
					<div className="time-tooltip">
						<p className="time-tooltip-costs">Costs</p>
						<p className="time-tooltip-bigNum">{distanceFromZero}</p>
						<p>{distanceFromZero === 1 ? 'minute' : 'minutes'}</p>
					</div>
				);
			}
			if (distanceFromZero === ZEROMINS) {
				return (
					<div className="time-tooltip">
						<p className="time-tooltip-costs">Costs</p>
						<p className="time-tooltip-bigNum">5+</p>
						<p>hours</p>
					</div>
				);
			}
			return (
				<div className="time-tooltip">
					<p className="time-tooltip-costs">Costs</p>
					<p className="time-tooltip-bigNum">
						{hours} {hours === 1 ? 'hour' : 'hours'}
					</p>
					<p>
						{distanceFromZero % HOUR ? `${distanceFromZero % HOUR}mins` : ''}
					</p>
				</div>
			);
		}
		if (value > ZEROMINS) {
			const distanceFromZero = value - ZEROMINS;
			const hours = Math.floor(distanceFromZero / HOUR);
			if (distanceFromZero < HOUR) {
				return (
					<div className="time-tooltip">
						<p className="time-tooltip-saves">Saves</p>
						<p className="time-tooltip-bigNum">{distanceFromZero}</p>
						<p>{distanceFromZero === 1 ? 'minute' : 'minutes'}</p>
					</div>
				);
			}
			if (distanceFromZero === ZEROMINS) {
				return (
					<div className="time-tooltip">
						<p className="time-tooltip-saves">Saves</p>
						<p className="time-tooltip-bigNum">5+</p>
						<p>hours</p>
					</div>
				);
			}
			return (
				<div className="time-tooltip">
					<p className="time-tooltip-saves">Saves</p>
					<p className="time-tooltip-bigNum">
						{hours} {hours === 1 ? 'hour' : 'hours'}
					</p>
					<p>
						{distanceFromZero % HOUR ? `${distanceFromZero % HOUR}mins` : ''}
					</p>
				</div>
			);
		}
	};

	const marks = [
		{ value: ZEROMINS, label: '' },
		{
			value: 0,
			label: (
				<div className="time-tooltip-leftLabel">
					<p>Costs time</p>
				</div>
			),
		},
		{
			value: MAX,
			label: (
				<div className="time-tooltip-rightLabel">
					<p>Saves time</p>
				</div>
			),
		},
	];

	return (
		<TimeSlider
			defaultValue={d || ZEROMINS}
			size="small"
			value={sliderValue}
			onChange={handleChange}
			track={false}
			aria-label="Default"
			valueLabelDisplay="auto"
			min={0}
			max={MAX}
			valueLabelFormat={formatValue}
			marks={marks}
			className="time-slider"
		/>
	);
};
